// Resolves the API base URL at runtime.
//
// Under Vite, `import.meta.env.VITE_API_URL` is statically replaced at build
// time. Under Jest (CommonJS), `import.meta` is not available, so this module
// falls back to the default. Tests can override by setting
// `globalThis.__MORIA_API_URL__` in a setup file.
//
// This helper lives in its own module so ts-jest can replace it with a mock
// when the test environment needs a specific base URL.
declare global {
  // Test-time override for the resolved API base URL.
  var __MORIA_API_URL__: string | undefined
  // Test-time override for the resolved events (SSE) base URL.
  var __MORIA_EVENTS_URL__: string | undefined
  // Test-time override for the local PDF transport selection.
  var __MORIA_FORCE_API_PDF_RANGE_TRANSPORT__: boolean | undefined
}

export function getApiBaseUrl(): string {
  if (typeof globalThis !== 'undefined' && typeof globalThis.__MORIA_API_URL__ === 'string') {
    return globalThis.__MORIA_API_URL__
  }
  // The Vite plugin rewrites this expression at build time. In any non-Vite
  // runtime (Jest, Node scripts) the Jest moduleNameMapper swaps this module
  // for tests/__mocks__/api-env.ts so this branch is never parsed in CJS.
  const env = typeof import.meta !== 'undefined' ? import.meta.env : undefined
  // In Vite dev (serve), all backend traffic must flow through the dev-server
  // proxy contributed by frontend/vite-plugins/dev-backend.ts. Returning the
  // page origin (e.g. http://localhost:5173) keeps both the generated
  // OpenAPI client and the hand-rolled `new URL(...)` call sites working,
  // because every request resolves to localhost:5173 and the Vite proxy
  // claims backend prefixes before they leave the dev server. The Developer
  // Backend Console can then switch the target backend without rebuilding.
  if (env && env.DEV && typeof window !== 'undefined') {
    return window.location.origin
  }
  // Accept any defined string — including ''. Empty string means "same
  // origin as the page" and falls through the URL builder as a relative
  // path. Undefined-only falls to the dev fallback below.
  if (env && typeof env.VITE_API_URL === 'string') {
    return env.VITE_API_URL
  }
  // Callers surface request failures through the approved browser reporter
  // once application telemetry is initialized.
  return 'http://localhost:8000'
}

/** Resolve the base URL for the SSE events service.
 *
 *  Falls back to the API base URL when unset — for local dev the api
 *  service mounts SSE too (SERVICE_ROLE=api is dual-mount). Hosted deploys
 *  use the API origin; the former events subdomain is mapped to the extractor.
 */
export function getEventsBaseUrl(): string {
  if (typeof globalThis !== 'undefined' && typeof globalThis.__MORIA_EVENTS_URL__ === 'string') {
    return globalThis.__MORIA_EVENTS_URL__
  }
  const env = typeof import.meta !== 'undefined' ? import.meta.env : undefined
  // Mirror the API-base behavior: in dev, route SSE through the Vite proxy
  // (which dispatches /events paths to the selected target's events URL).
  if (env && env.DEV && typeof window !== 'undefined') {
    return window.location.origin
  }
  if (env && typeof env.VITE_EVENTS_URL === 'string' && env.VITE_EVENTS_URL.length > 0) {
    return env.VITE_EVENTS_URL
  }
  return getApiBaseUrl()
}

/** Keep local pdf.js reads on the authenticated API's bounded 206 transport.
 *
 * fake-gcs accepts Range request headers but does not expose its range response
 * headers to browser JavaScript, so direct local URLs can silently degrade to a
 * full-PDF download. Hosted builds leave this disabled and retain direct GCS.
 */
export function shouldForceApiPdfRangeTransport(): boolean {
  if (
    typeof globalThis !== 'undefined' &&
    typeof globalThis.__MORIA_FORCE_API_PDF_RANGE_TRANSPORT__ === 'boolean'
  ) {
    return globalThis.__MORIA_FORCE_API_PDF_RANGE_TRANSPORT__
  }
  const env = typeof import.meta !== 'undefined' ? import.meta.env : undefined
  return env?.VITE_FORCE_API_PDF_RANGE_TRANSPORT === 'true'
}
